import { useEffect, useState } from "react";
import { apiFetch } from "../../../config/api";
import Loader from "../../../components/Loader";

// ── Role badge colours ────────────────────────────────────────────────────────
const ROLE_COLORS = {
  Admin:              { bg: "#fef2f2", color: "#b91c1c" },
  Director:           { bg: "#fdf4ff", color: "#7e22ce" },
  CXO:                { bg: "#eff6ff", color: "#1d4ed8" },
  Head:               { bg: "#f0f9ff", color: "#0369a1" },
  Manager:            { bg: "#fefce8", color: "#a16207" },
  Staff:              { bg: "#f8fafc", color: "#475569" },
};

const ROLE_LABELS = {};

function RoleBadge({ role }) {
  const style = ROLE_COLORS[role] || { bg: "#f3f4f6", color: "#374151" };
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: "0.05em",
      textTransform: "uppercase", padding: "2px 7px", borderRadius: 20,
      background: style.bg, color: style.color,
    }}>
      {ROLE_LABELS[role] || role}
    </span>
  );
}

// ── Initials avatar ───────────────────────────────────────────────────────────
function Avatar({ name, isCurrentUser, size = 36 }) {
  const initials = name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
      background: isCurrentUser ? "#2563eb" : "#e2e8f0",
      color: isCurrentUser ? "#fff" : "#475569",
      fontSize: size * 0.36, fontWeight: 700,
      border: isCurrentUser ? "2px solid #1d4ed8" : "2px solid #cbd5e1",
    }}>
      {initials}
    </div>
  );
}

// ── Single tree node ──────────────────────────────────────────────────────────
function TreeNode({ node, depth = 0 }) {
  const [open, setOpen] = useState(depth < 2);
  const hasChildren = node.directReports && node.directReports.length > 0;

  return (
    <div style={{ marginLeft: depth === 0 ? 0 : 24, marginTop: depth === 0 ? 0 : 8 }}>
      {/* Node card */}
      <div
        style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "10px 14px", borderRadius: 10,
          background: node.isCurrentUser
            ? "linear-gradient(135deg,#eff6ff,#f0f9ff)"
            : "var(--surface,#fff)",
          border: node.isCurrentUser
            ? "1.5px solid #93c5fd"
            : "1px solid var(--border,#e5e7eb)",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          cursor: hasChildren ? "pointer" : "default",
          transition: "box-shadow 0.15s",
          position: "relative",
        }}
        onClick={() => hasChildren && setOpen(o => !o)}
      >
        {/* Expand/collapse icon */}
        {hasChildren && (
          <span style={{
            fontSize: 11, color: "var(--text-3,#9ca3af)", userSelect: "none",
            width: 14, textAlign: "center", flexShrink: 0,
          }}>
            {open ? "▾" : "▸"}
          </span>
        )}
        {!hasChildren && <span style={{ width: 14, flexShrink: 0 }} />}

        <Avatar name={node.name} isCurrentUser={node.isCurrentUser} />

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{
              fontWeight: 600, fontSize: 13, color: "var(--text,#111)",
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {node.name}
            </span>
            {node.isCurrentUser && (
              <span style={{ fontSize: 10, color: "#2563eb", fontWeight: 700 }}>YOU</span>
            )}
            <RoleBadge role={node.role} />
          </div>
          <div style={{ display: "flex", gap: 12, marginTop: 2, flexWrap: "wrap" }}>
            {node.designation && (
              <span style={{ fontSize: 11, color: "var(--text-2,#6b7280)" }}>
                {node.designation}
              </span>
            )}
            {node.departmentName && (
              <span style={{ fontSize: 11, color: "var(--text-3,#9ca3af)" }}>
                · {node.departmentName}
              </span>
            )}
          </div>
        </div>

        {/* Active task count badge */}
        {node.activeTaskCount > 0 && (
          <span style={{
            fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
            background: "#fef3c7", color: "#92400e", flexShrink: 0,
          }}>
            {node.activeTaskCount} task{node.activeTaskCount !== 1 ? "s" : ""}
          </span>
        )}

        {hasChildren && (
          <span style={{
            fontSize: 11, color: "var(--text-3,#9ca3af)", flexShrink: 0,
          }}>
            {node.directReports.length} report{node.directReports.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* Children */}
      {hasChildren && open && (
        <div style={{
          marginLeft: 20, paddingLeft: 14,
          borderLeft: "2px solid var(--border,#e5e7eb)",
          marginTop: 4,
        }}>
          {node.directReports.map(child => (
            <TreeNode key={child.id} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Summary stats bar ─────────────────────────────────────────────────────────
function countNodes(node) {
  if (!node) return { total: 0, active: 0 };
  let total = 1;
  let active = node.activeTaskCount || 0;
  for (const child of (node.directReports || [])) {
    const sub = countNodes(child);
    total += sub.total;
    active += sub.active;
  }
  return { total, active };
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function MyTeamPage() {
  const [tree, setTree]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    apiFetch("/api/users/my-team")
      .then(r => r.ok ? r.json() : Promise.reject("Failed to load"))
      .then(data => setTree(data))
      .catch(() => setError("Could not load team hierarchy."))
      .finally(() => setLoading(false));
  }, []);

  // Flatten tree for search matching
  function filterTree(node, q) {
    if (!node) return null;
    const match = node.name.toLowerCase().includes(q) ||
      (node.designation || "").toLowerCase().includes(q) ||
      (node.departmentName || "").toLowerCase().includes(q) ||
      node.role.toLowerCase().includes(q);
    const filteredChildren = (node.directReports || [])
      .map(c => filterTree(c, q))
      .filter(Boolean);
    if (match || filteredChildren.length > 0) {
      return { ...node, directReports: filteredChildren };
    }
    return null;
  }

  const q = search.trim().toLowerCase();
  const displayTree = q && tree ? filterTree(tree, q) : tree;
  const stats = tree ? countNodes(tree) : null;

  return (
    <div style={{ maxWidth: 780, margin: "0 auto", padding: "24px 16px" }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "var(--text,#111)" }}>
          My Team
        </h2>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--text-2,#6b7280)" }}>
          Your organisation hierarchy — click any row to expand or collapse
        </p>
      </div>

      {/* Stats + search bar */}
      {stats && (
        <div style={{
          display: "flex", gap: 12, alignItems: "center",
          marginBottom: 16, flexWrap: "wrap",
        }}>
          <div style={{
            display: "flex", gap: 16, padding: "10px 16px", borderRadius: 10,
            background: "var(--surface,#fff)", border: "1px solid var(--border,#e5e7eb)",
            fontSize: 13,
          }}>
            <span><strong>{stats.total}</strong> <span style={{ color: "var(--text-2,#6b7280)" }}>people</span></span>
            <span style={{ color: "var(--border,#e5e7eb)" }}>|</span>
            <span><strong>{stats.active}</strong> <span style={{ color: "var(--text-2,#6b7280)" }}>active tasks</span></span>
          </div>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, role, dept…"
            style={{
              flex: 1, minWidth: 180, fontSize: 13,
              padding: "9px 12px", borderRadius: 8,
              border: "1px solid var(--border,#e5e7eb)",
              outline: "none", background: "var(--surface,#fff)",
            }}
          />
        </div>
      )}

      {/* Tree */}
      <div className="glass-card" style={{ padding: "16px 20px" }}>
        {loading && (
          <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
            <Loader />
          </div>
        )}
        {error && (
          <div style={{ color: "var(--danger-text,#b91c1c)", fontSize: 13, padding: 16 }}>
            {error}
          </div>
        )}
        {!loading && !error && !displayTree && q && (
          <div style={{ fontSize: 13, color: "var(--text-3,#9ca3af)", padding: "16px 0" }}>
            No results for "{search}"
          </div>
        )}
        {!loading && !error && displayTree && (
          <TreeNode node={displayTree} depth={0} />
        )}
      </div>
    </div>
  );
}
